import {
  Popover,
  PopoverContent,
  PopoverProps,
  PopoverTrigger,
  Stack,
  StackProps,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { Show } from '@pelicargo/ui';
import {
  Children,
  cloneElement,
  FocusEvent,
  isValidElement,
  JSX,
  MouseEvent,
  ReactNode,
  RefObject,
  useCallback,
  useEffect,
  useRef,
} from 'react';

import { useFieldValidationStates } from '../hooks/useFieldValidationStates';
import { LabeledText } from './LabeledText';
import { SimplePopoverInputType } from './SimpleLabeledPopoverInput';

export type SimplePopoverInputProps = Omit<StackProps, 'children'> & {
  label?: string | JSX.Element;
  children?: ReactNode | ((initialFocusRef: RefObject<HTMLElement>) => JSX.Element);
  displayValue: string | ReactNode;
  name?: string;
  onPopoverBlur?: () => void;
  popoverProps?: PopoverProps;
  isHighlighted?: boolean;
  highlightColor?: string;
  isRequired?: boolean;
  isSparkling?: boolean;
  isDisabled?: boolean;
  isHorizontal?: boolean;
  inputType?: SimplePopoverInputType;
};

export const SimplePopoverInput = (props: SimplePopoverInputProps) => {
  const {
    label,
    displayValue,
    children,
    onPopoverBlur,
    popoverProps,
    tabIndex,
    isHighlighted,
    highlightColor = 'yellow',
    isRequired = false,
    isSparkling = false,
    isHorizontal = false,
    isDisabled,
    inputType,
    ...rest
  } = props;
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isInvalid } = useFieldValidationStates(props?.name as string);
  const isOpenRef = useRef(isOpen);
  const contentRef = useRef<HTMLDivElement>(null);
  const ref = useRef<HTMLDivElement>(null);
  const initialFocusRef = useRef<HTMLElement>(null);
  const blurCheckFrameRef = useRef<number | null>(null);

  useEffect(() => {
    isOpenRef.current = isOpen;
  }, [isOpen]);

  useEffect(() => {
    return () => {
      if (blurCheckFrameRef.current != null) {
        cancelAnimationFrame(blurCheckFrameRef.current);
      }
    };
  }, []);

  const getCoord = useCallback((coord: 'height' | 'width') => {
    if (!ref.current) return 0;
    return ref.current.getBoundingClientRect()[coord];
  }, []);

  const hoverBorderColor = 'border.emphasized';
  const borderColor = isInvalid ? 'red.500' : 'transparent';

  const backgroundColor = isHighlighted ? highlightColor : 'transparent';

  const height = getCoord('height');
  const width = getCoord('width');

  const handleClose = useCallback(() => {
    if (!isOpenRef.current) return;

    isOpenRef.current = false;
    onPopoverBlur?.();
    onClose();
  }, [onClose, onPopoverBlur]);

  const handleFocusExit = useCallback(
    (event: FocusEvent<HTMLElement>) => {
      const nextFocused = event.relatedTarget as Node | null;
      if (!nextFocused) {
        if (blurCheckFrameRef.current != null) {
          cancelAnimationFrame(blurCheckFrameRef.current);
        }

        blurCheckFrameRef.current = requestAnimationFrame(() => {
          blurCheckFrameRef.current = null;

          const activeElement = document.activeElement;
          if (!activeElement) {
            handleClose();
            return;
          }

          if (ref.current?.contains(activeElement) || contentRef.current?.contains(activeElement)) {
            return;
          }

          handleClose();
        });
        return;
      }

      if (ref.current?.contains(nextFocused) || contentRef.current?.contains(nextFocused)) {
        return;
      }

      handleClose();
    },
    [handleClose],
  );

  const handleOpen = (event: MouseEvent<HTMLDivElement> | FocusEvent<HTMLDivElement>) => {
    event.preventDefault();

    onOpen();

    const id = requestAnimationFrame(() => {
      if (inputType !== 'select') return;

      const el = initialFocusRef.current;
      if (!el) return;

      // Focus first for screen readers + keyboard users
      el.focus();

      // Try the modern picker API (supported in Chromium)
      const withPicker = el as HTMLSelectElement & { showPicker?: () => void };
      try {
        if (typeof withPicker.showPicker === 'function') {
          withPicker.showPicker();
          return;
        }
      } catch {
        // Fall through to keyboard hint
      }

      // Fallback: hint open with ArrowDown. Not guaranteed for native selects.
      try {
        const ev = new KeyboardEvent('keydown', { key: 'ArrowDown', bubbles: true });
        el.dispatchEvent(ev);
      } catch {
        // no-op
      }
    });

    return () => cancelAnimationFrame(id);
  };

  // as="span" is needed in both places here to prevent the "<p> cannot appear as a descendant of <p>" react error.
  const formattedLabel = (
    <Text fontSize="xs" fontWeight="400" as="span">
      {label}
      <Show if={isRequired}>
        <Text fontSize="xs" fontWeight="400" color="red.300" as="span" ml={1}>
          *
        </Text>
      </Show>
    </Text>
  );

  // Support render prop pattern or clone children to pass the initialFocusRef
  const renderedChildren =
    typeof children === 'function'
      ? children(initialFocusRef)
      : Children.map(children, (child, index) => {
          if (index === 0 && isValidElement(child)) {
            return cloneElement(child, { ref: initialFocusRef } as any);
          }
          return child;
        });

  return (
    <Popover
      isOpen={isOpen}
      onClose={handleClose}
      placement="end-start"
      offset={[0, width * -1]}
      isLazy
      initialFocusRef={initialFocusRef}
      {...popoverProps}
    >
      <PopoverTrigger>
        <Stack
          spacing={0}
          py={1}
          px={2}
          borderRadius={8}
          borderWidth={1}
          borderColor={borderColor}
          backgroundColor={backgroundColor}
          cursor="default"
          _hover={{ borderColor: hoverBorderColor }}
          transition="border-color 100ms linear"
          opacity={isDisabled ? '0.8' : 1}
          pointerEvents={isDisabled ? 'none' : 'unset'}
          onBlurCapture={handleFocusExit}
          {...rest}
          ref={ref}
        >
          <LabeledText
            label={formattedLabel}
            value={displayValue ?? ''}
            onFocus={handleOpen}
            onClick={handleOpen}
            tabIndex={tabIndex}
            isSparkling={isSparkling}
            isHorizontal={isHorizontal}
          />
        </Stack>
      </PopoverTrigger>
      <PopoverContent
        minHeight={height}
        minWidth={width}
        p={2}
        pt={label ? 1 : 2}
        boxShadow="0 0 8px 0 rgba(0,0,0,0.15)"
        onBlurCapture={handleFocusExit}
        ref={contentRef}
      >
        <Stack spacing={0} direction={isHorizontal ? 'row' : 'column'}>
          <Stack w={isHorizontal ? 32 : undefined}>{label && formattedLabel}</Stack>
          <Stack flex={isHorizontal ? 1 : undefined}>{renderedChildren}</Stack>
        </Stack>
      </PopoverContent>
    </Popover>
  );
};
